import * as THREE from 'three';
import { MujocoApp } from '../test/app_clean';

interface Record360Options {
  fps?: number;
  duration?: number;
  exportSize?: { w: number; h: number };
  rotationOffsetX?: number; // radians, tilt forward/back
  positionOffsetY?: number; // world units, vertical shift
  zoomOffset?: number; // world units, + away, - toward target
}

export async function record360Preview(app: MujocoApp, options: Record360Options = {}) {
  const {
    fps = 60,
    duration = 5,
    exportSize = { w: 1280, h: 720 },
    rotationOffsetX = 0,
    positionOffsetY = 0,
    zoomOffset = 0,
  } = options;

  const { renderer, camera, controls, canvasContainer } = app;

  app.pauseRenderLoop();

  const prevStyle = { width: canvasContainer.style.width, height: canvasContainer.style.height };
  canvasContainer.style.width = `${exportSize.w}px`;
  canvasContainer.style.height = `${exportSize.h}px`;
  app.onResize();

  const startPos = camera.position.clone();
  const target = controls.target.clone();
  const upAxis = camera.up.clone().normalize();

  let offset = startPos.clone().sub(target); // target to camera

  if (rotationOffsetX !== 0) {
    const sideAxis = new THREE.Vector3().crossVectors(upAxis, offset).normalize();
    const q = new THREE.Quaternion().setFromAxisAngle(sideAxis, rotationOffsetX);
    offset.applyQuaternion(q);
  }

  // positionOffsetY: vertical shift doesnt work as intended
  offset.y += positionOffsetY;

  // zoomOffset: move away from target
  if (zoomOffset !== 0) {
    const dir = offset.clone().normalize();
    offset.addScaledVector(dir, zoomOffset);
  }

  camera.position.copy(target.clone().add(offset));
  camera.lookAt(target);
  controls.update();

  app.render();
  const pngURL = renderer.domElement.toDataURL('image/png');

  const stream = renderer.domElement.captureStream(fps);
  const recorder = new MediaRecorder(stream, {
    mimeType: 'video/webm; codecs=vp9',
    videoBitsPerSecond: 8_000_000,
  });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (e) => chunks.push(e.data);
  recorder.start();

  const startOffset = camera.position.clone().sub(target);

  // calculate total frames needed for exact duration
  // we want exactly (fps * duration) frames in the video
  // frame 0 should be at 0°, and frame (totalFrames - 1) should be at 360°/0°
  // to get perfect loop
  const totalFrames = Math.ceil(fps * duration);
  const frameInterval = 1000 / fps;

  await new Promise((resolve) => setTimeout(resolve, 100));

  let currentFrame = 0;
  const startTime = performance.now();

  await new Promise<void>((resolve) => {
    function animate() {
      // calculate progress: 0 to 1 (inclusive)
      // when currentFrame = 0, t = 0 (0°)
      // when currentFrame = totalFrames - 1, t = 1 (360°)
      //  render exactly totalFrames frames: 0, 1, ..., totalFrames - 1
      const t = totalFrames > 1 ? currentFrame / (totalFrames - 1) : 0;
      const angle = t * Math.PI * 2;

      const q = new THREE.Quaternion().setFromAxisAngle(upAxis, angle);
      const rotatedOffset = startOffset.clone().applyQuaternion(q);

      camera.position.copy(target).add(rotatedOffset);
      camera.lookAt(target);
      controls.update();

      app.render();

      currentFrame++;

      if (currentFrame < totalFrames) {
        const targetTime = startTime + currentFrame * frameInterval;
        const now = performance.now();
        const delay = Math.max(0, targetTime - now);

        if (delay > 16) {
          // if delay is significant, use setTimeout for precision
          setTimeout(animate, delay);
        } else {
          // for small delays, use requestAnimationFrame to stay in sync with browser
          requestAnimationFrame(animate);
        }
      } else {
        // all frames rendered
        // last frame should be 360°/0°
        // render one more frame at 0° to ensure perfect loop
        const angle = 0;

        const q = new THREE.Quaternion().setFromAxisAngle(upAxis, angle);
        const rotatedOffset = startOffset.clone().applyQuaternion(q);

        camera.position.copy(target).add(rotatedOffset);
        camera.lookAt(target);
        controls.update();

        app.render();

        // give MediaRecorder extra time to ensure all frames including the loop frame are captured
        // use 3 frame intervals to ensure the last frame is fully encoded
        setTimeout(() => resolve(), frameInterval * 3);
      }
    }
    animate();
  });

  recorder.stop();
  const webmBlob: Blob = await new Promise((resolve) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: 'video/webm' }));
  });

  // restore camera & canvas
  camera.position.copy(startPos);
  controls.target.copy(target);
  controls.update();
  canvasContainer.style.width = prevStyle.width;
  canvasContainer.style.height = prevStyle.height;
  app.onResize();
  app.resumeRenderLoop();

  return { pngURL, webmURL: URL.createObjectURL(webmBlob) };
}
